import { ChangeDetectionStrategy, ChangeDetectorRef, Component, OnDestroy } from '@angular/core';
import { FormControl } from '@angular/forms';
import { BehaviorSubject, Observable, Subscription } from 'rxjs';
import { debounceTime, distinctUntilChanged, map } from 'rxjs/operators';
import {
  OdooBitacoraService, OdooDatosTecnicos, OdooGrupoDia, OdooMovimiento, OdooMovimientosFiltro, agruparPorDia,
} from 'src/app/services/odoo/odoo-bitacora.service';
import { OdooCarga } from 'src/app/services/odoo/odoo.models';
import { conCarga, mensajeDeFallo } from 'src/app/services/odoo/odoo.service';

interface VistaBitacora { cargando: boolean; error: string | null; hayDatos: boolean; total: number; conProblema: number; grupos: OdooGrupoDia[]; }
interface Tecnico { cargando: boolean; error: string | null; datos: OdooDatosTecnicos | null; }

const fHora = new Intl.DateTimeFormat('es-VE', { hour: 'numeric', minute: '2-digit', timeZone: 'America/Caracas' });

/** BITÁCORA — la memoria de lo que pasó con Odoo, en frases y agrupada por día. Se guarda 180 días. */
@Component({
  selector: 'app-odoo-bitacora',
  templateUrl: './odoo-bitacora.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class OdooBitacoraComponent implements OnDestroy {
  readonly limite = 25;
  filtro: OdooMovimientosFiltro = { sobre: '', ok: '', q: '', page: 1, limit: this.limite };
  readonly buscar = new FormControl('');
  abierto: string | null = null;
  tecnicos: Readonly<Record<string, Tecnico>> = {};
  tecnicoAbierto: ReadonlySet<string> = new Set<string>();
  readonly esperando = [0, 1, 2, 3, 4, 5];

  readonly sobres: { valor: OdooMovimientosFiltro['sobre']; texto: string }[] = [
    { valor: '', texto: 'Todo' }, { valor: 'cliente', texto: 'Clientes' }, { valor: 'comercio', texto: 'Comercios' },
    { valor: 'archivo', texto: 'Archivos' }, { valor: 'conexion', texto: 'Conexión' },
  ];
  readonly resultados: { valor: OdooMovimientosFiltro['ok']; texto: string }[] = [
    { valor: '', texto: 'Todos' }, { valor: 'true', texto: 'Bien' }, { valor: 'false', texto: 'Con problema' },
  ];

  private readonly params$ = new BehaviorSubject<OdooMovimientosFiltro>(this.filtro);
  private readonly sub: Subscription;
  readonly vm$: Observable<VistaBitacora> = conCarga(this.params$, (f) => this.api.movimientos(f)).pipe(
    map((c: OdooCarga<import('src/app/services/odoo/odoo-bitacora.service').OdooMovimientosPagina>): VistaBitacora => ({
      cargando: c.cargando, error: c.error, hayDatos: !!c.datos,
      total: c.datos ? c.datos.total : 0,
      conProblema: c.datos ? c.datos.con_problema : 0,
      grupos: c.datos ? agruparPorDia(c.datos.items) : [],
    })),
  );

  constructor(private api: OdooBitacoraService, private cdr: ChangeDetectorRef) {
    this.sub = this.buscar.valueChanges.pipe(debounceTime(300), distinctUntilChanged()).subscribe((q: string | null) => this.cambiar({ q: q || '', page: 1 }));
  }

  ngOnDestroy(): void { this.sub.unsubscribe(); }

  cambiar(parcial: Partial<OdooMovimientosFiltro>): void {
    this.filtro = { ...this.filtro, ...parcial };
    this.abierto = null;
    this.params$.next(this.filtro);
  }
  elegirSobre(v: string): void { this.cambiar({ sobre: v as OdooMovimientosFiltro['sobre'], page: 1 }); }
  elegirResultado(v: string): void { this.cambiar({ ok: v as OdooMovimientosFiltro['ok'], page: 1 }); }
  recargar(): void { this.tecnicos = {}; this.tecnicoAbierto = new Set<string>(); this.params$.next(this.filtro); }
  totalPaginas(total: number): number { return Math.max(1, Math.ceil(total / this.limite)); }

  alternar(id: string): void { this.abierto = this.abierto === id ? null : id; }

  /** Despliega «datos técnicos»; los pide al servidor solo la primera vez. */
  alternarTecnico(id: string): void {
    const n = new Set(this.tecnicoAbierto);
    if (n.has(id)) { n.delete(id); this.tecnicoAbierto = n; return; }
    n.add(id);
    this.tecnicoAbierto = n;
    const previo = this.tecnicos[id];
    if (previo && (previo.datos || previo.cargando)) return;
    this.tecnicos = { ...this.tecnicos, [id]: { cargando: true, error: null, datos: null } };
    this.api.tecnico(id).subscribe({
      next: (datos) => { this.tecnicos = { ...this.tecnicos, [id]: { cargando: false, error: null, datos } }; this.cdr.markForCheck(); },
      error: (e: unknown) => { this.tecnicos = { ...this.tecnicos, [id]: { cargando: false, error: mensajeDeFallo(e), datos: null } }; this.cdr.markForCheck(); },
    });
  }

  hora(iso: string): string { return fHora.format(new Date(iso)); }

  duracion(ms: number | null): string {
    if (ms === null || ms === undefined) return '—';
    return ms < 1000 ? `${ms} ms` : `${(ms / 1000).toFixed(1).replace('.', ',')} s`;
  }

  respuestaSimple(m: OdooMovimiento): string {
    if (m.ok) return 'Odoo contestó que todo estuvo bien.';
    if (!m.status) return 'Odoo no llegó a contestar.';
    return `Odoo contestó que no (código ${m.status}).`;
  }

  porGrupo = (_: number, g: OdooGrupoDia): string => g.clave;
  porId = (_: number, m: OdooMovimiento): string => m._id;
  porValor = (_: number, o: { valor: string }): string => o.valor;
  porIndice = (i: number): number => i;
}
